/**
 * Enterprise Channel Partners Page
 *
 * Full management page for channel partners within an enterprise:
 * - List all channel partners
 * - Create/Edit/Delete channel partners (admin only)
 * - View organizations per channel partner
 * - View stats and quotas
 *
 * IMPORTANT: Enterprise users cannot access clinical data (PHI) - HIPAA compliance
 *
 * Uses the shared DataTable component with inline action buttons.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Users,
  Plus,
  Search,
  Edit2,
  Trash2,
  Eye,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  Briefcase,
  X,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { apiService } from '@/services/api';
import { toast } from 'react-hot-toast';
import { DataTable, ColumnDefinition, ActionDefinition, SortState } from '@/components/shared/DataTable';
import { slugify } from '@/utils/slugify';

interface ChannelPartner {
  id: string;
  name: string;
  slug: string;
  type: 'reseller' | 'referral' | 'distributor' | 'oem' | 'white_label';
  status: 'active' | 'suspended' | 'cancelled';
  contact: {
    primaryName: string;
    primaryEmail: string;
    primaryPhone?: string;
    address?: {
      street?: string;
      city?: string;
      state?: string;
      country?: string;
      postalCode?: string;
    };
  };
  quotas: {
    maxOrganizations: number;
    maxUsersPerOrg: number;
    maxStorageGB: number;
  };
  features: {
    whiteLabeling: boolean;
    customBranding: boolean;
    apiAccess: boolean;
    advancedAnalytics: boolean;
  };
  stats: {
    organizationCount: number;
    userCount: number;
  };
  createdAt: string;
  updatedAt: string;
}

interface ChannelPartnerFormData {
  name: string;
  slug: string;
  type: string;
  contact: {
    primaryName: string;
    primaryEmail: string;
    primaryPhone: string;
  };
  quotas: {
    maxOrganizations: number;
    maxUsersPerOrg: number;
    maxStorageGB: number;
  };
  features: {
    whiteLabeling: boolean;
    customBranding: boolean;
    apiAccess: boolean;
    advancedAnalytics: boolean;
  };
}

const initialFormData: ChannelPartnerFormData = {
  name: '',
  slug: '',
  type: 'reseller',
  contact: {
    primaryName: '',
    primaryEmail: '',
    primaryPhone: ''
  },
  quotas: {
    maxOrganizations: 10,
    maxUsersPerOrg: 50,
    maxStorageGB: 100
  },
  features: {
    whiteLabeling: false,
    customBranding: false,
    apiAccess: false,
    advancedAnalytics: false
  }
};

const EnterpriseChannelPartners: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [channelPartners, setChannelPartners] = useState<ChannelPartner[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState<ChannelPartner | null>(null);
  const [formData, setFormData] = useState<ChannelPartnerFormData>(initialFormData);
  const [saving, setSaving] = useState(false);
  const [sortState, setSortState] = useState<SortState>({ column: 'name', direction: 'asc' });

  const isAdmin = user?.role === 'enterprise_admin';

  const loadChannelPartners = useCallback(async () => {
    try {
      setLoading(true);
      interface Response { success: boolean; data: { channelPartners: ChannelPartner[] } }
      const res = await apiService.get<Response>('/v1/enterprise/channel-partners');
      setChannelPartners(res.data?.channelPartners || []);
    } catch (error) {
      console.error('Failed to load channel partners:', error);
      toast.error('Failed to load channel partners');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadChannelPartners();
  }, [loadChannelPartners]);

  const handleCreatePartner = () => {
    setFormData(initialFormData);
    setEditingPartner(null);
    setShowCreateModal(true);
  };

  const handleEditPartner = useCallback((partner: ChannelPartner) => {
    setFormData({
      name: partner.name,
      slug: partner.slug,
      type: partner.type,
      contact: {
        primaryName: partner.contact.primaryName || '',
        primaryEmail: partner.contact.primaryEmail || '',
        primaryPhone: partner.contact.primaryPhone || ''
      },
      quotas: {
        maxOrganizations: partner.quotas.maxOrganizations,
        maxUsersPerOrg: partner.quotas.maxUsersPerOrg,
        maxStorageGB: partner.quotas.maxStorageGB
      },
      features: {
        whiteLabeling: partner.features.whiteLabeling,
        customBranding: partner.features.customBranding,
        apiAccess: partner.features.apiAccess,
        advancedAnalytics: partner.features.advancedAnalytics
      }
    });
    setEditingPartner(partner);
    setShowCreateModal(true);
  }, []);

  const handleDeletePartner = useCallback(async (partnerId: string) => {
    if (!confirm('Are you sure you want to delete this channel partner? This action will affect all associated organizations.')) {
      return;
    }

    try {
      await apiService.delete(`/v1/enterprise/channel-partners/${partnerId}`);
      toast.success('Channel partner deleted successfully');
      loadChannelPartners();
    } catch (error) {
      console.error('Failed to delete channel partner:', error);
      toast.error('Failed to delete channel partner');
    }
  }, [loadChannelPartners]);

  const handleSavePartner = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingPartner) {
        await apiService.put(`/v1/enterprise/channel-partners/${editingPartner.id}`, formData);
        toast.success('Channel partner updated successfully');
      } else {
        await apiService.post('/v1/enterprise/channel-partners', formData);
        toast.success('Channel partner created successfully');
      }
      setShowCreateModal(false);
      loadChannelPartners();
    } catch (error: any) {
      console.error('Failed to save channel partner:', error);
      toast.error(error.message || 'Failed to save channel partner');
    } finally {
      setSaving(false);
    }
  };


  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'suspended':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadgeClasses = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'suspended':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeBadgeClasses = (type: string) => {
    switch (type) {
      case 'reseller':
        return 'bg-blue-100 text-blue-800';
      case 'distributor':
        return 'bg-purple-100 text-purple-800';
      case 'white_label':
        return 'bg-amber-100 text-amber-800';
      case 'oem':
        return 'bg-indigo-100 text-indigo-800';
      case 'referral':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredPartners = channelPartners.filter(partner => {
    const matchesSearch = partner.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      partner.slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
      partner.contact.primaryEmail.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || partner.status === statusFilter;
    const matchesType = typeFilter === 'all' || partner.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  // Sorted data
  const sortedPartners = useMemo(() => {
    if (!sortState.column || !sortState.direction) return filteredPartners;

    const getValue = (obj: any, path: string) =>
      path.split('.').reduce((acc, part) => acc?.[part], obj);

    return [...filteredPartners].sort((a, b) => {
      const aVal = getValue(a, sortState.column!);
      const bVal = getValue(b, sortState.column!);
      if (aVal == null) return sortState.direction === 'asc' ? 1 : -1;
      if (bVal == null) return sortState.direction === 'asc' ? -1 : 1;
      const cmp = typeof aVal === 'string'
        ? aVal.localeCompare(bVal)
        : Number(aVal) - Number(bVal);
      return sortState.direction === 'asc' ? cmp : -cmp;
    });
  }, [filteredPartners, sortState]);

  // DataTable column definitions
  const columns: ColumnDefinition<ChannelPartner>[] = useMemo(() => [
    {
      id: 'name',
      header: 'Channel Partner',
      sortable: true,
      sortKey: 'name',
      accessor: (partner) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{partner.name}</div>
          <div className="text-xs text-gray-500">{partner.slug}</div>
        </div>
      )
    },
    {
      id: 'type',
      header: 'Type',
      sortable: true,
      sortKey: 'type',
      hideOnTablet: true, // Only show on desktop (≥1024px)
      accessor: (partner) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getTypeBadgeClasses(partner.type)}`}>
          {partner.type.replace('_', ' ')}
        </span>
      )
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      sortKey: 'status',
      accessor: (partner) => (
        <div className="flex items-center gap-2">
          {getStatusIcon(partner.status)}
          <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatusBadgeClasses(partner.status)}`}>
            {partner.status}
          </span>
        </div>
      )
    },
    {
      id: 'organizations',
      header: 'Organizations',
      sortable: true,
      sortKey: 'stats.organizationCount',
      accessor: (partner) => (
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-700">{partner.stats?.organizationCount || 0}</span>
          {partner.quotas.maxOrganizations !== -1 && (
            <span className="text-xs text-gray-400">/ {partner.quotas.maxOrganizations}</span>
          )}
        </div>
      )
    },
    {
      id: 'users',
      header: 'Users',
      sortable: true,
      sortKey: 'stats.userCount',
      hideOnTablet: true, // Only show on desktop (≥1024px)
      accessor: (partner) => (
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-700">{partner.stats?.userCount || 0}</span>
        </div>
      )
    },
    {
      id: 'contact',
      header: 'Contact',
      sortable: true,
      sortKey: 'contact.primaryName',
      hideOnTablet: true, // Only show on desktop (≥1024px)
      accessor: (partner) => (
        <div>
          <div className="text-sm text-gray-900">{partner.contact.primaryName}</div>
          <div className="text-xs text-gray-500">{partner.contact.primaryEmail}</div>
        </div>
      )
    }
  ], []);

  // DataTable action definitions (inline buttons instead of dropdown)
  const actions: ActionDefinition<ChannelPartner>[] = useMemo(() => {
    if (!isAdmin) return [];

    return [
      {
        id: 'view',
        icon: <Eye className="h-4 w-4" />,
        tooltip: 'View Details',
        onClick: (partner) => navigate(`/enterprise/partners/${partner.id}`),
        variant: 'default'
      },
      {
        id: 'edit',
        icon: <Edit2 className="h-4 w-4" />,
        tooltip: 'Edit',
        onClick: (partner) => handleEditPartner(partner),
        variant: 'primary'
      },
      {
        id: 'delete',
        icon: <Trash2 className="h-4 w-4" />,
        tooltip: 'Delete',
        onClick: (partner) => handleDeletePartner(partner.id),
        variant: 'danger'
      }
    ];
  }, [isAdmin, navigate, handleEditPartner, handleDeletePartner]);

  // Render expanded content for each partner
  const renderExpandedContent = (partner: ChannelPartner) => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-4 bg-gray-50">
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Contact Information</h4>
        <p className="text-sm text-gray-600">{partner.contact.primaryName}</p>
        <p className="text-sm text-gray-600">{partner.contact.primaryEmail}</p>
        {partner.contact.primaryPhone && (
          <p className="text-sm text-gray-600">{partner.contact.primaryPhone}</p>
        )}
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Quotas</h4>
        <p className="text-sm text-gray-600">
          Organizations: {partner.quotas.maxOrganizations === -1 ? 'Unlimited' : partner.quotas.maxOrganizations}
        </p>
        <p className="text-sm text-gray-600">
          Users per Org: {partner.quotas.maxUsersPerOrg === -1 ? 'Unlimited' : partner.quotas.maxUsersPerOrg}
        </p>
        <p className="text-sm text-gray-600">
          Storage: {partner.quotas.maxStorageGB === -1 ? 'Unlimited' : `${partner.quotas.maxStorageGB} GB`}
        </p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Features</h4>
        <div className="flex flex-wrap gap-2">
          {partner.features.whiteLabeling && (
            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">White Labeling</span>
          )}
          {partner.features.customBranding && (
            <span className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded">Custom Branding</span>
          )}
          {partner.features.apiAccess && (
            <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">API Access</span>
          )}
          {partner.features.advancedAnalytics && (
            <span className="px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded">Advanced Analytics</span>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Channel Partners</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage your enterprise's channel partner network
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadChannelPartners}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            title="Refresh"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
          {isAdmin && (
            <button
              onClick={handleCreatePartner}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add Channel Partner
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, slug, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="reseller">Reseller</option>
              <option value="distributor">Distributor</option>
              <option value="white_label">White Label</option>
              <option value="oem">OEM</option>
              <option value="referral">Referral</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Partners</p>
          <p className="text-2xl font-semibold text-gray-900">{channelPartners.length}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Active</p>
          <p className="text-2xl font-semibold text-green-600">
            {channelPartners.filter(p => p.status === 'active').length}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Organizations</p>
          <p className="text-2xl font-semibold text-blue-600">
            {channelPartners.reduce((sum, p) => sum + (p.stats?.organizationCount || 0), 0)}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Users</p>
          <p className="text-2xl font-semibold text-violet-600">
            {channelPartners.reduce((sum, p) => sum + (p.stats?.userCount || 0), 0)}
          </p>
        </div>
      </div>

      {/* Partners Table - Using shared DataTable component */}
      <DataTable
        data={sortedPartners}
        columns={columns}
        actions={actions}
        loading={loading}
        emptyMessage="No channel partners found"
        emptyIcon={<Briefcase className="h-12 w-12 text-gray-300" />}
        expandable
        expandedIds={expandedIds}
        onExpansionChange={setExpandedIds}
        renderExpanded={renderExpandedContent}
        sortable={true}
        sortState={sortState}
        onSortChange={setSortState}
        hoverable
        striped={false}
        ariaLabel="Channel Partners table"
      />

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowCreateModal(false)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingPartner ? 'Edit Channel Partner' : 'Create Channel Partner'}
                </h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <form onSubmit={handleSavePartner} className="p-6 space-y-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-700">Basic Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            name: e.target.value,
                            slug: editingPartner ? formData.slug : slugify(e.target.value)
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Slug *</label>
                      <input
                        type="text"
                        value={formData.slug}
                        onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        required
                        disabled={!!editingPartner}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Type *</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    >
                      <option value="reseller">Reseller</option>
                      <option value="distributor">Distributor</option>
                      <option value="white_label">White Label</option>
                      <option value="oem">OEM</option>
                      <option value="referral">Referral</option>
                    </select>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-700">Contact Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Contact Name *</label>
                      <input
                        type="text"
                        value={formData.contact.primaryName}
                        onChange={(e) => setFormData({
                          ...formData,
                          contact: { ...formData.contact, primaryName: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Contact Email *</label>
                      <input
                        type="email"
                        value={formData.contact.primaryEmail}
                        onChange={(e) => setFormData({
                          ...formData,
                          contact: { ...formData.contact, primaryEmail: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={formData.contact.primaryPhone}
                      onChange={(e) => setFormData({
                        ...formData,
                        contact: { ...formData.contact, primaryPhone: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Quotas */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-700">Quotas</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Max Organizations</label>
                      <input
                        type="number"
                        value={formData.quotas.maxOrganizations}
                        onChange={(e) => setFormData({
                          ...formData,
                          quotas: { ...formData.quotas, maxOrganizations: parseInt(e.target.value) || -1 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        min="-1"
                      />
                      <p className="text-xs text-gray-400 mt-1">-1 for unlimited</p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Max Users/Org</label>
                      <input
                        type="number"
                        value={formData.quotas.maxUsersPerOrg}
                        onChange={(e) => setFormData({
                          ...formData,
                          quotas: { ...formData.quotas, maxUsersPerOrg: parseInt(e.target.value) || -1 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        min="-1"
                      />
                      <p className="text-xs text-gray-400 mt-1">-1 for unlimited</p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Storage (GB)</label>
                      <input
                        type="number"
                        value={formData.quotas.maxStorageGB}
                        onChange={(e) => setFormData({
                          ...formData,
                          quotas: { ...formData.quotas, maxStorageGB: parseInt(e.target.value) || -1 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        min="-1"
                      />
                      <p className="text-xs text-gray-400 mt-1">-1 for unlimited</p>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-700">Features</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.features.whiteLabeling}
                        onChange={(e) => setFormData({
                          ...formData,
                          features: { ...formData.features, whiteLabeling: e.target.checked }
                        })}
                        className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                      />
                      <span className="text-sm text-gray-700">White Labeling</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.features.customBranding}
                        onChange={(e) => setFormData({
                          ...formData,
                          features: { ...formData.features, customBranding: e.target.checked }
                        })}
                        className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                      />
                      <span className="text-sm text-gray-700">Custom Branding</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.features.apiAccess}
                        onChange={(e) => setFormData({
                          ...formData,
                          features: { ...formData.features, apiAccess: e.target.checked }
                        })}
                        className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                      />
                      <span className="text-sm text-gray-700">API Access</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.features.advancedAnalytics}
                        onChange={(e) => setFormData({
                          ...formData,
                          features: { ...formData.features, advancedAnalytics: e.target.checked }
                        })}
                        className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                      />
                      <span className="text-sm text-gray-700">Advanced Analytics</span>
                    </label>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : (editingPartner ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnterpriseChannelPartners;
